import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCountry } from "../contexts/CountryContext";
import { API_URL } from "../lib/supabase";
import { MapPin, Star, Shield, Clock, CheckCircle, ChevronLeft, Award, TrendingDown, BadgeCheck } from "lucide-react";
import { formatDateNZ } from "../lib/format";
import { getCountryConfig } from "../lib/country";
import AttachmentViewer from "../components/attachments/AttachmentViewer";

const QuoteDetailPage = () => {
  const { isAuthenticated, session, loading: authLoading } = useAuth();
  const { country } = useCountry();
  const { showMarketplaceActivity, showProviderDirectory } = getCountryConfig(country?.code);
  const showActivity = showMarketplaceActivity !== false;
  const showProviderLinks = showProviderDirectory !== false;
  const navigate = useNavigate();
  const { quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reviewForm, setReviewForm] = useState({ providerId: null, rating: 5, comment: '' });
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchQuote();
  }, [isAuthenticated, authLoading, quoteId]);

  const fetchQuote = async () => {
    try {
      const res = await fetch(`${API_URL}/quotes/${quoteId}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (!res.ok) throw new Error('Not found');
      setQuote(await res.json());
    } catch (e) { setError('Quote not found'); }
    finally { setLoading(false); }
  };

  const updateStatus = async (newStatus) => {
    setStatusLoading(true);
    try {
      const res = await fetch(`${API_URL}/quotes/${quoteId}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) await fetchQuote();
    } catch (e) { console.error(e); }
    finally { setStatusLoading(false); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.comment.trim()) return;
    setReviewLoading(true);
    try {
      const res = await fetch(`${API_URL}/reviews`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider_id: reviewForm.providerId,
          quote_request_id: quoteId,
          rating: reviewForm.rating,
          comment: reviewForm.comment,
        })
      });
      const data = await res.json();
      if (res.ok) {
        setReviewSuccess(reviewForm.providerId);
        setReviewForm({ providerId: null, rating: 5, comment: '' });
        fetchQuote();
      } else {
        alert(data.detail || 'Failed to submit review');
      }
    } catch (e) { console.error(e); }
    finally { setReviewLoading(false); }
  };

  const STATUS = {
    'new': { label: 'Submitted', color: '#f59e0b', bg: '#fef3c7' },
    'sent': { label: 'Sent to Providers', color: '#6366f1', bg: '#e0e7ff' },
    'responded': { label: 'Responses Received', color: '#065f46', bg: '#d1fae5' },
    'closed': { label: 'Completed', color: '#047857', bg: '#d1fae5' },
    'canceled': { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6' },
  };
  const cfg = quote ? (STATUS[quote.status] || { label: quote.status, color: '#6b7280', bg: '#f3f4f6' }) : {};

  if (authLoading || loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="page" style={{ textAlign: 'center', padding: '60px 20px' }}><h2>Quote not found</h2><Link to="/my-quotes" className="btn-primary" style={{ marginTop: 16, display: 'inline-block' }}>Back to My Quotes</Link></div>;

  const responses = quote.responses || [];
  const reviewedProviders = quote.reviewed_providers || [];
  const canReview = ['responded', 'closed'].includes(quote.status);

  return (
    <div className="page" data-testid="quote-detail-page" style={{ maxWidth: '800px', margin: '0 auto' }}>
      {/* Back nav */}
      <button onClick={() => navigate('/my-quotes')} data-testid="back-to-quotes" style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '0', marginBottom: '16px', fontSize: '0.8125rem' }}>
        <ChevronLeft size={16} /> Back to My Quotes
      </button>

      {/* Header */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '20px 24px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 4px' }} data-testid="quote-service-name">{quote.services?.name || 'Service Request'}</h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: 0 }}>
              {quote.services?.categories?.name && <span>{quote.services.categories.name} · </span>}
              Submitted {formatDateNZ(quote.created_at)}
            </p>
          </div>
          <span data-testid="quote-status-badge" style={{ padding: '4px 14px', borderRadius: 14, fontSize: '0.8125rem', fontWeight: 600, color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
        </div>
      </div>

      {/* Details */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '20px 24px', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '10px' }}>Job Details</h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-dark)', lineHeight: 1.7, marginBottom: '14px' }} data-testid="quote-description">{quote.job_description}</p>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><MapPin size={14} /> {quote.city}, {quote.country_code}</span>
          {quote.preferred_date && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Clock size={14} /> {quote.preferred_date}</span>}
          {quote.preferred_time && <span>Time: {quote.preferred_time}</span>}
          <span>{quote.providers_matched || 0} provider{(quote.providers_matched || 0) !== 1 ? 's' : ''} matched</span>
        </div>
        <AttachmentViewer quoteId={quoteId} accessToken={session?.access_token} title="Your Attachments" />
      </div>

      {/* Status actions */}
      {quote.status === 'responded' && responses.length > 0 && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 'var(--radius-md)', padding: '14px 20px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <p style={{ fontSize: '0.8125rem', color: '#1e40af', margin: 0 }}>You have {responses.length} response{responses.length > 1 ? 's' : ''}. Compare providers and mark as completed when the work is done.</p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => updateStatus('closed')} disabled={statusLoading} className="btn-primary" style={{ fontSize: '0.8125rem', padding: '6px 14px' }} data-testid="complete-btn">Mark as Completed</button>
            <button onClick={() => updateStatus('canceled')} disabled={statusLoading} className="btn-outline" style={{ fontSize: '0.8125rem', padding: '6px 14px' }} data-testid="cancel-btn">Cancel Request</button>
          </div>
        </div>
      )}

      {/* Provider Comparison Section */}
      {responses.length >= 2 && (() => {
        const amounts = responses.filter(r => r.quoted_amount).map(r => r.quoted_amount);
        const ratings = responses.filter(r => r.providers?.average_rating).map(r => r.providers.average_rating);
        const exps = responses.filter(r => r.providers?.years_experience).map(r => r.providers.years_experience);
        const lowestPrice = amounts.length ? Math.min(...amounts) : null;
        const highestRating = ratings.length ? Math.max(...ratings) : null;
        const mostExp = exps.length ? Math.max(...exps) : null;
        return (
          <div data-testid="comparison-section" style={{ marginBottom: '20px' }}>
            <h2 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '12px' }}>
              Compare Providers <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({responses.length})</span>
            </h2>
            <div className="comparison-grid" data-testid="comparison-grid">
              {responses.map(resp => {
                const prov = resp.providers || {};
                const isLowest = resp.quoted_amount && resp.quoted_amount === lowestPrice && amounts.filter(a => a === lowestPrice).length === 1;
                const isTopRated = prov.average_rating && prov.average_rating === highestRating && ratings.filter(r => r === highestRating).length === 1;
                const isMostExp = prov.years_experience && prov.years_experience === mostExp && exps.filter(e => e === mostExp).length === 1;
                const isVerified = prov.is_id_verified;
                return (
                  <div key={resp.id} data-testid={`compare-card-${resp.id}`} className="compare-card">
                    {/* Badges */}
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', minHeight: '22px', marginBottom: '10px' }}>
                      {isLowest && <span data-testid={`badge-lowest-${resp.id}`} className="compare-badge compare-badge--price"><TrendingDown size={11} /> Best Price</span>}
                      {showActivity && isTopRated && <span data-testid={`badge-rated-${resp.id}`} className="compare-badge compare-badge--rating"><Award size={11} /> Top Rated</span>}
                      {isMostExp && <span data-testid={`badge-exp-${resp.id}`} className="compare-badge compare-badge--exp"><Shield size={11} /> Most Experienced</span>}
                    </div>
                    {/* Provider name + verified */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      {showProviderLinks ? (
                        <Link to={`/providers/${prov.id}`} className="compare-card__name" data-testid={`compare-name-${resp.id}`}>{prov.business_name || 'Provider'}</Link>
                      ) : (
                        <span className="compare-card__name" data-testid={`compare-name-${resp.id}`}>{prov.business_name || 'Provider'}</span>
                      )}
                      {isVerified && <BadgeCheck size={15} strokeWidth={2.5} color="#047857" data-testid={`compare-verified-${resp.id}`} />}
                    </div>
                    {/* Rating + reviews */}
                    {!showActivity ? null : prov.average_rating ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8125rem', marginBottom: '8px' }}>
                        <Star size={13} fill="#f59e0b" stroke="#f59e0b" />
                        <span style={{ fontWeight: 600, color: 'var(--text-dark)' }}>{prov.average_rating}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>({prov.review_count || 0} reviews)</span>
                      </div>
                    ) : <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '8px' }}>No ratings yet</div>}
                    {/* Price */}
                    {resp.quoted_amount ? (
                      <div className="compare-card__price" data-testid={`compare-price-${resp.id}`}>
                        {country.currencySymbol}{Number(resp.quoted_amount).toLocaleString()}
                        <span className="compare-card__currency">{(resp.currency || country.currency).toUpperCase()}</span>
                      </div>
                    ) : <div className="compare-card__price" style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Price on request</div>}
                    {/* Meta row */}
                    <div className="compare-card__meta">
                      {prov.city && <span><MapPin size={11} /> {prov.city}</span>}
                      {prov.years_experience && <span>{prov.years_experience}yr exp</span>}
                      {resp.estimated_duration && <span><Clock size={11} /> {resp.estimated_duration}</span>}
                    </div>
                    {/* Message preview */}
                    <p className="compare-card__msg">{(resp.message || '').slice(0, 100)}{resp.message?.length > 100 ? '...' : ''}</p>
                    {/* CTA */}
                    <button className="compare-card__cta" data-testid={`compare-view-${resp.id}`}
                      onClick={() => { document.getElementById(`response-full-${resp.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}>
                      View Full Response
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Provider Responses (Full Detail) */}
      <h2 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '12px' }}>Provider Responses {responses.length > 0 && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({responses.length})</span>}</h2>
      {responses.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
          {responses.map(resp => {
            const prov = resp.providers || {};
            const alreadyReviewed = reviewedProviders.includes(prov.id);
            const showReviewForm = reviewForm.providerId === prov.id;
            const justReviewed = reviewSuccess === prov.id;
            return (
              <div key={resp.id} id={`response-full-${resp.id}`} data-testid={`response-${resp.id}`} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '18px 20px' }}>
                {/* Provider header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    {showProviderLinks ? (
                      <Link to={`/providers/${prov.id}`} style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--text-dark)', textDecoration: 'none' }} data-testid={`provider-name-${resp.id}`}>
                        {prov.business_name || 'Provider'}
                      </Link>
                    ) : (
                      <span style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--text-dark)' }} data-testid={`provider-name-${resp.id}`}>
                        {prov.business_name || 'Provider'}
                      </span>
                    )}
                    <div style={{ display: 'flex', gap: '10px', fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                      {prov.city && <span>{prov.city}</span>}
                      {prov.years_experience > 0 && <span>{prov.years_experience}yr exp</span>}
                      {showActivity && prov.average_rating && <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}><Star size={11} fill="#f59e0b" stroke="#f59e0b" /> {prov.average_rating} ({prov.review_count || 0})</span>}
                    </div>
                  </div>
                  {resp.quoted_amount && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--navy)' }} data-testid={`quoted-amount-${resp.id}`}>{country.currencySymbol}{Number(resp.quoted_amount).toLocaleString()}</div>
                      <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{(resp.currency || country.currency).toUpperCase()}</div>
                    </div>
                  )}
                </div>
                {/* Message */}
                <p style={{ fontSize: '0.8125rem', color: 'var(--text-dark)', lineHeight: 1.6, marginBottom: '8px' }}>{resp.message}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  <span>{resp.estimated_duration && `Est. ${resp.estimated_duration}`}</span>
                  <span>{formatDateNZ(resp.created_at)}</span>
                </div>
                {/* Review section */}
                {canReview && !alreadyReviewed && !justReviewed && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
                    {!showReviewForm ? (
                      <button onClick={() => setReviewForm({ ...reviewForm, providerId: prov.id })} className="btn-outline" style={{ fontSize: '0.75rem', padding: '4px 12px' }} data-testid={`write-review-${prov.id}`}>
                        <Star size={12} style={{ marginRight: 4 }} /> Write a Review
                      </button>
                    ) : (
                      <form onSubmit={submitReview} data-testid={`review-form-${prov.id}`}>
                        <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                          {[1,2,3,4,5].map(i => (
                            <Star key={i} size={22} fill={i <= reviewForm.rating ? '#f59e0b' : 'none'} stroke={i <= reviewForm.rating ? '#f59e0b' : '#d1d5db'}
                              style={{ cursor: 'pointer' }} onClick={() => setReviewForm({ ...reviewForm, rating: i })} data-testid={`star-${i}`} />
                          ))}
                        </div>
                        <textarea value={reviewForm.comment} onChange={e => setReviewForm({ ...reviewForm, comment: e.target.value })}
                          placeholder="Share your experience with this provider..."
                          data-testid="review-comment" rows={3}
                          style={{ width: '100%', padding: '10px', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', fontSize: '0.8125rem', resize: 'vertical', marginBottom: '8px' }} />
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button type="submit" disabled={reviewLoading} className="btn-primary" style={{ fontSize: '0.8125rem', padding: '6px 14px' }} data-testid="submit-review">
                            {reviewLoading ? 'Submitting...' : 'Submit Review'}
                          </button>
                          <button type="button" onClick={() => setReviewForm({ ...reviewForm, providerId: null })} className="btn-outline" style={{ fontSize: '0.8125rem', padding: '6px 14px' }}>Cancel</button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
                {(alreadyReviewed || justReviewed) && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)', color: '#047857', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <CheckCircle size={14} /> Review submitted
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '20px' }}>
          <p>No responses yet. Providers have been notified and will respond shortly.</p>
        </div>
      )}
    </div>
  );
};

export default QuoteDetailPage;
